import { Router } from '@angular/router';
import { NimmstaCommonsModuleConfiguration } from '../nimmsta-commons-module.config';
import { tryGetImpersonationData } from './impersonation';
import {
  IMPERSONATION_SESSION_ENDED,
  IMPERSONATION_SESSION_STORAGE_KEY,
} from './constants.impersonation';
import Keycloak from 'keycloak-js';
import { INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG } from 'keycloak-angular';
import { inject, provideAppInitializer } from '@angular/core';

function initializeKeycloak(
  keycloak: Keycloak,
  configuration: NimmstaCommonsModuleConfiguration,
  router: Router,
): () => Promise<void> {
  // wrap the initialization in a function to be able to use async/await
  return async () => {
    try {
      // Try to get the impersonation data
      const impersonationData = await tryGetImpersonationData();

      // If we have impersonation data, set in sessionStorage a property to allow for full-page reloads
      if (impersonationData) {
        sessionStorage.setItem(
          IMPERSONATION_SESSION_STORAGE_KEY,
          JSON.stringify(impersonationData),
        );

        // register a listener when the user closes the browser tab to trigger a beacon call (which terminates the session)
        // unfortunately, this also triggers when the user reloads the page, as we can't distinguish between the two
        window.addEventListener('pagehide', (event) => {
          if (!event.persisted) {
            sessionStorage.removeItem(IMPERSONATION_SESSION_STORAGE_KEY);
            sessionStorage.setItem(IMPERSONATION_SESSION_ENDED, 'true');

            if (keycloak.didInitialize) {
              navigator.sendBeacon(
                `${configuration.apiUrls.workflow!}/api/v1/keycloak/session/${
                  keycloak.tokenParsed?.['sid']
                }/terminate`,
                new URLSearchParams({ access_token: keycloak.token! }),
              );
            }
          }
        });
      }

      // Parse the state fragment to get the kc_action_status (if present)
      const state = window.location.hash;
      const stateParams = new URLSearchParams(state);
      const kcActionStatus = stateParams.get('kc_action_status');
      const kcError = stateParams.get('error');
      if (kcActionStatus !== null) {
        // Set the kc_action_status in sessionStorage to allow other components to react to it
        sessionStorage.setItem('kc_action_status', kcActionStatus);
      }
      if (kcError !== null) {
        // Set the kc_error in sessionStorage to allow other components to react to it
        sessionStorage.setItem('kc_error', kcError);
      }

      // Initialize Keycloak
      await keycloak.init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
        // See https://www.npmjs.com/package/keycloak-angular#setup
        silentCheckSsoRedirectUri: `${window.location.origin}/assets/keycloak/silent-check-sso.html`,

        // If impersonation data is present, use it to login
        token: impersonationData?.access_token,
        refreshToken: impersonationData?.refresh_token,
        idToken: impersonationData?.id_token,
      });
    } catch (error) {
      console.error('Error initializing Keycloak', error);
      router.navigate(['/500']);
    }
  };
}

export const provideKeycloakInitializer = () =>
  provideAppInitializer(() =>
    initializeKeycloak(inject(Keycloak), inject(NimmstaCommonsModuleConfiguration), inject(Router))(),
  );

export const provideKeycloakBearerInterceptorConfig = (environment: {
  apiUrls: Record<string, string>;
}) => {
  // Collect unique origins from apiUrls (ignoring falsy/invalid values)
  const origins = Array.from(
    new Set(
      Object.entries(environment.apiUrls)
        .map(([, u]) => u)
        .filter((u): u is string => !!u)
        .map((u) => {
          try {
            return new URL(u).origin;
          } catch {
            return null;
          }
        })
        .filter((o): o is string => !!o),
    ),
  );

  // Escape for use in a single RegExp
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const sources = origins.map(escapeRegex).join('|');
  const regex = new RegExp(`^(${sources})(\\/.*)?$`, 'i');

  return {
    provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
    useValue: [
      {
        urlPattern: regex,
        bearerPrefix: 'Bearer',
      },
    ],
  };
};
